import React, { useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { Link } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { CalendarCheck2, CheckSquare, Clock, Maximize2Icon, User, Users2, X } from "lucide-react"

// ** Internal Element
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { ScrollArea } from "@components/common/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Types
import { DictType, Task } from "@pma/types"

// ** Page Routes
import { PROJECTS_PAGE_ROUTE, USERS_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Helpers
import { cn, formatStringWithLink, getUserFromId } from "@pma/utils/helpers"

// ** Store
import { useAuth } from "@stores/auth-store"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Types
import { ExpandTaskProps } from "../../types"

// ** API Routes
import { NOTES_BY_TASK_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Context
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

// ** Custom Components
import TaskEditor from "./task-editor"
import DateEditor from "./date-editor"
import AddSubItem from "./add-sub-task"
import TimeTracker from "./time-tracker"
import AssignToUser from "./assign-to-user"
import TaskStatusEditor from "./task-status-editor"
import AttachFileToTask from "./task-attached-files"
import { TaskActivity } from "../activities/task-activity"
import NotesThread from "@components/notifications/notes-thread"
import NoteTextArea from "@components/notifications/note-text-area"

const TABS = ["subitems", "notes", "files", "activities"] as const
type TabType = (typeof TABS)[number]

const TAB_LABELS: Record<TabType, string> = {
  subitems: "SubItems",
  notes: "Notes",
  files: "Files",
  activities: "Activities",
}

const ExpandTask = ({ task, children, row }: ExpandTaskProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const organizationUsers = authConfig?.organizationUsers

  const { fetchSubTaskMutation } = React.useContext(GroupedTasksContext)

  const [flag, setFlag] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>("subitems")
  const [sortedNotes, setSortedNotes] = useState<DictType[]>([])

  const getNotes = async () => {
    const response = await axios.get(SERVER_URL + NOTES_BY_TASK_ID_ENDPOINT + task.id, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
    return response.data
  }

  const { data } = useQuery({
    queryKey: ["get-notes-for-expand", orgId],
    enabled: flag,
    queryFn: getNotes,
  })

  const subTask = useMemo(() => {
    if (!fetchSubTaskMutation?.data) return undefined
    return fetchSubTaskMutation.data.data.find((s: DictType) => s.id === task.id)
  }, [fetchSubTaskMutation?.data, task.id])

  // Merge task with the latest task data from notes API so fields like
  // linked_files_id stay in sync after the get-notes-for-expand query refetches.
  const updatedTask = useMemo(() => {
    const baseTask = task.parent_tasks_id == 0 ? task : subTask ?? task
    // console.log('data', data)
    if (data?.task) {
      // console.log('data', data)
      return { ...baseTask, ...data.task }
    }
    return baseTask
  }, [task, subTask, data])

  const responsibleUser = getUserFromId(updatedTask?.creator_id, organizationUsers)
  const subItems = updatedTask?._subtasks

  const handleClose = () => setSortedNotes([])

  useEffect(() => {
    handleClose()
    if (data?.notes && data.notes.length > 0) {
      const foundNotes =
        [...data.notes].sort((a: DictType, b: DictType) => b.created_at - a.created_at) || []
      setSortedNotes(foundNotes.length > 0 ? foundNotes : [])
    }
  }, [data])

  const renderInfoRow = (
    Icon: typeof Users2,
    label: string,
    content: React.ReactNode,
    inlineContent = false,
  ) => (
    <div className='my-4 flex items-center gap-4 text-xs'>
      <div className='flex items-center gap-2'>
        <Icon size={14} />
        <span>{translate(label)}</span>
        {inlineContent && content}
      </div>
      {!inlineContent && content}
    </div>
  )

  return (
    <Tooltip>
      <Popover onOpenChange={setFlag}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {children ? (
              children
            ) : (
              <Button
                variant={"ghost"}
                className='h-6 w-6 rounded-full text-xs hover:border-red-500 hover:bg-slate-300 hover:bg-transparent hover:text-red-400'
                size={"icon"}
              >
                <Maximize2Icon size={14} />
              </Button>
            )}
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent className='flex items-center gap-1 bg-secondary-foreground text-black'>
          <h4>
            {translate(`Expand`)} {updatedTask?.title}
          </h4>
        </TooltipContent>
        <PopoverContent
          className={cn(
            "mx-2 h-[400px] w-[300px] overflow-y-scroll sm:w-[360px] md:m-0 md:w-[450px]",
            isHebrew ? "rtl" : "ltr",
          )}
        >
          <div className='w-full items-center text-xs font-light text-pms_black'>
            <div className='flex items-center justify-between'>
              <div>
                <span>{translate("Projects")}</span>
                <span className='mx-1'>/</span>
                <Link to={PROJECTS_PAGE_ROUTE + `/${updatedTask?.project?.id}`}>
                  <Button variant={"link"} className='m-0 p-1 text-xs'>
                    {updatedTask?.project?.title}
                  </Button>
                </Link>
              </div>
              <PopoverClose onClick={handleClose}>
                <X size={16} />
              </PopoverClose>
            </div>
          </div>

          <TaskEditor id={updatedTask?.id} title={"title"} value={updatedTask?.title}>
            <div
              className='w-fit text-left'
              dangerouslySetInnerHTML={{ __html: formatStringWithLink(updatedTask?.title) }}
            />
          </TaskEditor>

          <TaskEditor id={updatedTask?.id} title={"description"} value={updatedTask?.description}>
            <p className='text-left text-xs text-slate-500'>
              {updatedTask?.dec ? (
                <div
                  className='w-fit text-left'
                  dangerouslySetInnerHTML={{ __html: formatStringWithLink(updatedTask?.dec) }}
                />
              ) : (
                "---"
              )}
            </p>
          </TaskEditor>

          {renderInfoRow(
            Users2,
            "Assigned",
            updatedTask?.responsible_id && (
              <AssignToUser
                taskId={updatedTask?.id}
                responsible={updatedTask?.responsible_id || []}
              />
            ),
          )}

          {renderInfoRow(
            CalendarCheck2,
            "Duration",
            <DateEditor
              value={{ from: updatedTask?.start_at, to: updatedTask?.finish_to }}
              taskId={updatedTask?.id}
            />,
          )}

          {renderInfoRow(Clock, "Time", <TimeTracker task={updatedTask as Task} />)}

          {renderInfoRow(
            CheckSquare,
            "Status",
            <TaskStatusEditor status={updatedTask?.status} taskId={updatedTask?.id} />,
            true,
          )}

          {renderInfoRow(
            User,
            "Creator",
            <Link
              className='flex items-center gap-1'
              to={USERS_PAGE_ROUTE + `/${responsibleUser?.id}`}
            >
              <img
                alt=''
                src={
                  responsibleUser?.user_picture?.url
                    ? responsibleUser.user_picture.url
                    : `${process.env.PUBLIC_URL}/ionaddoutline@2x.png`
                }
                className='h-6 w-6 rounded-full'
              />
              {responsibleUser?.name}
            </Link>,
            true,
          )}

          <div className='my-4'>
            <div className='flex w-[200px] gap-1 sm:w-[260px]'>
              {TABS.map((tab) => (
                <Button
                  key={tab}
                  variant={"ghost"}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "m-0 h-fit min-w-[60px] rounded-none p-0 text-[10px] hover:bg-transparent sm:min-w-[80px]",
                    activeTab === tab && "border-b-2 border-primary",
                  )}
                >
                  {translate(TAB_LABELS[tab])}
                </Button>
              ))}
            </div>
            <Separator />
            <ScrollArea className='h-[250px] pb-6' scrollHideDelay={0}>
              {activeTab === "subitems" && (
                <div className='py-2'>
                  <div className='flex w-full items-center justify-end'>
                    <small>{translate("Add new")}</small>
                    <AddSubItem task={updatedTask} />
                  </div>
                  {subItems?.length > 0 &&
                    subItems.map((item: DictType, index: number) => (
                      <div key={item.id}>
                        <div
                          className={cn(
                            "flex w-full items-center justify-between gap-3 py-2",
                            isHebrew ? "rtl" : "ltr",
                          )}
                        >
                          <div className='flex items-center gap-1'>
                            <small>{index + 1}.</small>
                            <h3>{item.title}</h3>
                          </div>
                          <TaskStatusEditor status={item?.status} taskId={item?.id} />
                        </div>
                        <Separator />
                      </div>
                    ))}
                </div>
              )}

              {activeTab === "notes" && (
                <div className='flex flex-col py-2'>
                  {sortedNotes?.length > 0 && (
                    <NotesThread notes={sortedNotes} fromExpandCalling={true} />
                  )}
                  <div className='my-2' />
                  <NoteTextArea
                    focus={!sortedNotes?.length}
                    task={updatedTask}
                    fromExpandCalling={true}
                  />
                </div>
              )}

              {activeTab === "activities" && <TaskActivity task={updatedTask} />}

              {activeTab === "files" && (
                <AttachFileToTask
                  taskId={updatedTask?.id}
                  files={updatedTask?.linked_files_id}
                  size='lg'
                  showAsFolder={false}
                />
              )}
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
    </Tooltip>
  )
}

export default ExpandTask
